import { useEffect, useRef, useState, Fragment, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as _ from 'lodash';
import { isEmpty } from 'lodash';
import { Tabs, Button, Empty, Typography, Space, Select, Spin } from 'antd';
import './EvaluationList.less';
import EvaluationDrawer from '../../Expert/evaluation/components/EvaluationDrawer';
import { conferenceOnceApi, getConferenceEvaluationListApi } from '@services/Conference';
import { localStorageGet } from '@core/cookie';
import PageHeader from '@components/PageHeader/PageHeader';
import { CONFERENCE_WORKSPACE_PARENT_PATH, CONFERENCE_WORKSPACE_ROUTE_BASE } from '@/layouts/conferenceWorkspaceConfig';
const { Text } = Typography;

const EVALUATION_STATUS_OPTIONS = [
    { label: '未评价', value: 'unevaluated' },
    { label: '已评价', value: 'evaluated' },
];

/** 收集当前日期 tab 下所有可展开场次 ID */
function collectHallIds(halls) {
    const ids = [];
    halls?.forEach(val => {
        if (val.expertAgendaEvaluationList?.length) {
            if (val.id != null) {
                ids.push(val.id);
            }
        }
        val.secondHallsList?.forEach(s => {
            if (s.expertAgendaEvaluationList?.length && s.id != null) {
                ids.push(s.id);
            }
        });
    });
    return ids;
}

/** 收集当前日期 tab 下全部日程任务，用于筛选项 */
function collectAgendaItems(halls) {
    const items = [];
    halls?.forEach(val => {
        if (val.expertAgendaEvaluationList) {
            items.push(...val.expertAgendaEvaluationList);
        }
        val.secondHallsList?.forEach(s => {
            if (s.expertAgendaEvaluationList) {
                items.push(...s.expertAgendaEvaluationList);
            }
        });
    });
    return items;
}

function matchesTabFilter(item, filters) {
    const expertNames = filters?.expertNames?.length ? filters.expertNames : null;
    if (expertNames && !expertNames.includes(item.expertName)) {
        return false;
    }
    const taskNames = filters?.taskNames?.length ? filters.taskNames : null;
    if (taskNames && !taskNames.includes(item.taskName)) {
        return false;
    }
    const recordCount = item.evaluationCount ?? 0;
    if (filters?.evaluationStatus === 'unevaluated' && recordCount >= 1) {
        return false;
    }
    if (filters?.evaluationStatus === 'evaluated' && recordCount < 1) {
        return false;
    }
    return true;
}

function EvaluationDateTabContent({ halls, expandedHallIds, onExpandedChange, filters, onFiltersChange, onEvaluation }) {
    const allHallIds = useMemo(() => collectHallIds(halls), [halls]);

    const { expertNameOptions, taskNameOptions } = useMemo(() => {
        const items = collectAgendaItems(halls);
        const expertNames = [...new Set(items.map(i => i.expertName).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'zh-CN'));
        const taskNames = [...new Set(items.map(i => i.taskName).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'zh-CN'));
        return {
            expertNameOptions: expertNames.map(name => ({ label: name, value: name })),
            taskNameOptions: taskNames.map(name => ({ label: name, value: name })),
        };
    }, [halls]);

    const isHallExpanded = useCallback(
        hallId => {
            if (expandedHallIds === undefined) {
                return true;
            }
            return expandedHallIds.has(hallId);
        },
        [expandedHallIds],
    );

    const handleHallToggle = useCallback(
        hallId => {
            onExpandedChange(prev => {
                let next = prev;
                if (next === undefined) {
                    next = new Set(allHallIds);
                } else {
                    next = new Set(next);
                }
                if (next.has(hallId)) {
                    next.delete(hallId);
                } else {
                    next.add(hallId);
                }
                return next;
            });
        },
        [allHallIds, onExpandedChange],
    );

    const handleExpandAll = () => {
        onExpandedChange(new Set(allHallIds));
    };

    const handleCollapseAll = () => {
        onExpandedChange(new Set());
    };

    const filterAgendaList = list => (list ?? []).filter(v => matchesTabFilter(v, filters));

    const renderAgendaItems = list =>
        filterAgendaList(list).map(v => (
            <div className="hall-agenda-item" key={v.id}>
                <div className="hall-agenda-title">
                    <i className="bi bi-calendar"></i> {v.agendaName}
                </div>
                <div className="hall-agenda-info">
                    <span>嘉宾：{v.expertName}</span>
                    <span>任务：{v.taskName}</span>
                    <span>
                        记录人数：
                        {!!v.evaluationCount ? `${v.evaluationCount}人` : <Text type="secondary">暂无记录</Text>}
                    </span>
                    {v.isEvaluated ? (
                        <i>已记录</i>
                    ) : (
                        <Button className="evaluation-btn" type="link" size="small" onClick={() => onEvaluation(v)}>
                            记录价值
                        </Button>
                    )}
                </div>
            </div>
        ));

    const updateFilter = (field, value) => {
        const next = { ...filters };
        if (Array.isArray(value)) {
            if (value.length) {
                next[field] = value;
            } else {
                delete next[field];
            }
        } else if (value !== undefined && value !== null && value !== '') {
            next[field] = value;
        } else {
            delete next[field];
        }
        onFiltersChange(next);
    };

    const hasActiveFilter = !!(filters?.expertNames?.length || filters?.taskNames?.length || filters?.evaluationStatus);

    const handleResetFilters = () => {
        onFiltersChange({});
    };

    return (
        <div className="evaluation-halls">
            <div className="evaluation-halls-toolbar">
                <Space wrap size="small" className="evaluation-halls-toolbar-left">
                    <Select
                        mode="multiple"
                        size="small"
                        allowClear
                        showSearch
                        maxTagCount="responsive"
                        placeholder="专家姓名"
                        optionFilterProp="label"
                        className="evaluation-halls-filter-select evaluation-halls-filter-select--multiple"
                        value={filters?.expertNames}
                        options={expertNameOptions}
                        onChange={value => updateFilter('expertNames', value)}
                    />
                    <Select
                        mode="multiple"
                        size="small"
                        allowClear
                        showSearch
                        maxTagCount="responsive"
                        placeholder="日程任务类型"
                        optionFilterProp="label"
                        className="evaluation-halls-filter-select evaluation-halls-filter-select--multiple"
                        value={filters?.taskNames}
                        options={taskNameOptions}
                        onChange={value => updateFilter('taskNames', value)}
                    />
                    <Select size="small" allowClear placeholder="评价状态" className="evaluation-halls-filter-select" value={filters?.evaluationStatus} options={EVALUATION_STATUS_OPTIONS} onChange={value => updateFilter('evaluationStatus', value)} />
                    <Button size="small" disabled={!hasActiveFilter} onClick={handleResetFilters}>
                        重置
                    </Button>
                </Space>
                <Space wrap size="small" className="evaluation-halls-toolbar-right">
                    <Button size="small" onClick={handleExpandAll}>
                        全部展开
                    </Button>
                    <Button size="small" onClick={handleCollapseAll}>
                        全部收起
                    </Button>
                </Space>
            </div>
            {halls.map((val, valIdx) => {
                const hasFirstHall = !!val.expertAgendaEvaluationList && val.expertAgendaEvaluationList.length > 0;
                const hasSecondHalls = !!val.secondHallsList && val.secondHallsList.length > 0;
                if (!hasFirstHall && !hasSecondHalls) {
                    return null;
                }
                const valKey = val.id != null ? val.id : `hall-val-${valIdx}`;
                const firstHallFiltered = filterAgendaList(val.expertAgendaEvaluationList);
                return (
                    <Fragment key={valKey}>
                        {hasFirstHall
                            ? firstHallFiltered.length > 0 && (
                                  <div className="hall">
                                      <h3 className="hall-title" onClick={() => handleHallToggle(val.id)}>
                                          <i className={`bi ${isHallExpanded(val.id) ? 'bi-caret-down-fill' : 'bi-caret-right-fill'} hall-title-caret`}></i>
                                          {val.name}
                                      </h3>
                                      {isHallExpanded(val.id) && renderAgendaItems(val.expertAgendaEvaluationList)}
                                  </div>
                              )
                            : val.secondHallsList.map(s => {
                                  const secondFiltered = filterAgendaList(s.expertAgendaEvaluationList);
                                  if (!secondFiltered.length) {
                                      return null;
                                  }
                                  return (
                                      <div className="hall" key={s.id}>
                                          <h3 className="hall-title" onClick={() => handleHallToggle(s.id)}>
                                              <i className={`bi ${isHallExpanded(s.id) ? 'bi-caret-down-fill' : 'bi-caret-right-fill'} hall-title-caret`}></i>
                                              {s.name}
                                          </h3>
                                          {isHallExpanded(s.id) && renderAgendaItems(s.expertAgendaEvaluationList)}
                                      </div>
                                  );
                              })}
                    </Fragment>
                );
            })}
        </div>
    );
}

export default function EvaluationList() {
    const params = useParams();
    let conferenceId = params?.id || 0; // 会议项目 ID
    conferenceId = parseInt(conferenceId, 10) || 0;

    const navigate = useNavigate();

    const [conference, setConference] = useState(null);

    const [evaluationData, setEvaluationData] = useState({});
    const [listLoading, setListLoading] = useState(true);
    const [visibleEvaluationDrawer, setVisibleEvaluationDrawer] = useState(false); // 记录价值 Drawer
    const currentDate = useRef(null);
    const [tabActiveKey, setTabActiveKey] = useState('');
    const [currentEvaluation, setCurrentEvaluation] = useState({});
    /** 各日期 tab 下展开的场次 ID；undefined 表示默认全部展开 */
    const [expandedHallIdsByTab, setExpandedHallIdsByTab] = useState({});
    /** 各日期 tab 内按专家姓名、任务类型筛选 */
    const [tabFiltersByDate, setTabFiltersByDate] = useState({});

    // 会议日期tab切换
    const onChangeTabs = value => {
        currentDate.current = value;
    };

    const onTabClick = key => setTabActiveKey(key);

    // 记录价值
    const handleEvaluation = item => {
        setCurrentEvaluation(item);
        setVisibleEvaluationDrawer(true);
    };

    const handleTabFiltersChange = useCallback((dateKey, nextFilters) => {
        setTabFiltersByDate(prev => ({ ...prev, [dateKey]: nextFilters }));
    }, []);

    const handleExpandedChange = useCallback((dateKey, updater) => {
        setExpandedHallIdsByTab(prev => {
            const current = prev[dateKey];
            const next = typeof updater === 'function' ? updater(current) : updater;
            return { ...prev, [dateKey]: next };
        });
    }, []);

    // 获取会议专家综评列表
    const getConferenceEvaluationList = async () => {
        setListLoading(true);
        try {
            const res = await getConferenceEvaluationListApi({ conferenceId });
            if (res.status_code === 200) {
                const evaluationData = res.data;
                setEvaluationData(evaluationData ?? {});
                // 列表刷新后恢复默认：当前 tab 下全部场次展开
                setExpandedHallIdsByTab({});

                if (!_.isEmpty(evaluationData)) {
                    const agendaDates = Object.keys(evaluationData);
                    if (!currentDate.current) {
                        if (agendaDates?.length) {
                            currentDate.current = agendaDates[0];
                            setTabActiveKey(agendaDates[0]);
                        } else {
                            currentDate.current = null;
                            setTabActiveKey('');
                        }
                    }
                }
            }
        } finally {
            setListLoading(false);
        }
    };

    // 获取项目信息
    const getConferenceInfo = async conferenceId => {
        const res = await conferenceOnceApi({ id: conferenceId });
        if (res.status_code === 200) {
            setConference(res.data);
        }
    };

    useEffect(() => {
        if (!conferenceId) {
            setListLoading(false);
            setEvaluationData({});
            return;
        }
        getConferenceInfo(conferenceId);
        getConferenceEvaluationList(); // 专家价值列表
    }, [conferenceId]);

    // 是否拥有评价专家参会情况
    useEffect(() => {
        const roleList = localStorageGet('roleList') ? JSON.parse(localStorageGet('roleList')) : [];
        if (roleList?.length) {
            const hasEvaluation = roleList.some(item => item.name === '评价专家参会情况');
            if (hasEvaluation) {
                return;
            }
            navigate(`/conf/conferences`);
        }
    }, [navigate]);

    useEffect(() => {
        document.title = '专家价值-医会议';
    }, []);

    return (
        <>
            <PageHeader title="专家价值" breadcrumb={[{ label: '医会议', path: CONFERENCE_WORKSPACE_PARENT_PATH }, { label: conference?.name || '项目信息', path: `${CONFERENCE_WORKSPACE_ROUTE_BASE}/${conferenceId}` }, '专家价值']} />

            <div className="page-route-body evaluation-wrap">
                <Spin spinning={listLoading} delay={300} wrapperClassName="evaluation-list-spin">
                    {!isEmpty(evaluationData) && (
                        <div className="evaluation-area">
                            <Tabs
                                onChange={onChangeTabs}
                                size="small"
                                type="card"
                                activeKey={tabActiveKey}
                                onTabClick={onTabClick}
                                items={Object.keys(evaluationData)?.map(item => {
                                    return {
                                        label: item,
                                        key: item,
                                        children: (
                                            <EvaluationDateTabContent
                                                halls={evaluationData[item]}
                                                expandedHallIds={expandedHallIdsByTab[item]}
                                                onExpandedChange={updater => handleExpandedChange(item, updater)}
                                                filters={tabFiltersByDate[item] || {}}
                                                onFiltersChange={next => handleTabFiltersChange(item, next)}
                                                onEvaluation={handleEvaluation}
                                            />
                                        ),
                                    };
                                })}
                            />
                        </div>
                    )}

                    {!listLoading && isEmpty(evaluationData) && (
                        <div className="no-evaluation">
                            <Empty description={<Text type="secondary">暂无记录价值任务</Text>} />
                        </div>
                    )}
                </Spin>
            </div>

            <EvaluationDrawer visibleEvaluationDrawer={visibleEvaluationDrawer} setVisibleEvaluationDrawer={setVisibleEvaluationDrawer} currentEvaluation={currentEvaluation} getExpertEvaluationList={getConferenceEvaluationList} />
        </>
    );
}
